/**
 * Local-only persistence for reading-progression mastery.
 * See specs/word-mastery-progression.md §5. No account, no cloud — the player's progress
 * lives in localStorage and can be wiped from the menu (with a confirmation).
 *
 * Keyed per LESSON (pack id + language, e.g. `reading-250__de`, `wortarten__de`) so every
 * lesson in every language has its own independent journey. `de` and `de-CH` share word ids
 * (the Swiss pool is derived from the German one), but keep separate stores per spec O-2.
 *
 * Legacy note: progress used to be keyed by bare language tag (`abcs.mastery.v1.de`). The
 * reading-journey store is migrated forward to its lesson key on first load — see
 * `migrateMasteryKey` and ReadingProgression's constructor.
 */
import { MAX_BOX } from './config';
import { requestPersistentStorage } from '@/platform/persistStorage';

export type WordState = 'locked' | 'learning' | 'review' | 'mastered';

export interface WordStat {
  box: number;            // Leitner box 1..MAX_BOX
  state: WordState;
  seen: number;           // boards this word appeared on
  correct: number;        // correct resolutions (popped-as-target or avoided-as-decoy)
  wrong: number;          // wrong resolutions (popped-as-decoy)
  streak: number;         // current consecutive-correct
  bestStreak: number;
  introducedAt: number | null;
  lastSeenAt: number | null;
  masteredAt: number | null;
  sessionsSeen: number;   // distinct sessions this word appeared in (gates mastery)
  lastSession: number;    // session counter value at last appearance (0 = never)
  // MasteryData.boardSeq at the last board this word appeared on (0 = never). Drives the spacing
  // rule in ReadingProgression.weightFor — the "repeat twice close together, then further apart"
  // curve. Persisted rather than kept in memory precisely because a game ends on lives: without
  // it, every new game forgot what the previous one had just shown.
  lastBoardSeq: number;
}

export interface ProfileStat {
  firstPlayedAt: number | null;
  lastPlayedAt: number | null;
  totalCorrect: number;
  totalWrong: number;
  days: string[];                 // distinct calendar days played (YYYY-MM-DD)
  abcPlanetReachedAt: number | null;
  // When the full-screen CelebrationScene was actually shown for this lesson. Tracked separately
  // from `abcPlanetReachedAt` because the two can diverge: the planet is stamped mid-round, on the
  // pop that masters the last word, while the celebration can only be shown at an exit. If the
  // child leaves via Back — or the app is killed — before reaching one, this stays null and the
  // celebration is still owed. See ReadingProgression.celebrationPending().
  celebrationShownAt: number | null;
  // UFO "treat" bank, fed by two independent sources. `ufosUnlocked` is a monotonic high-water
  // mark of floor(stars/3) (never decreases on star regression); `ufosBonus` counts the ones
  // earned by reaching Mars in a single game (UFO_AT_STACK_STARS); `ufosClaimed` counts UFOs
  // spent on Survival runs. Available = unlocked + bonus - claimed.
  //
  // The bonus needs its own counter precisely BECAUSE `ufosUnlocked` is a high-water mark: adding
  // to it would raise the mark the star track is compared against, so the next star-earned UFO
  // would be swallowed instead of granted — the child would gain a bonus and lose a star reward.
  ufosUnlocked: number;
  ufosBonus: number;
  ufosClaimed: number;
  // 💎 Diamond bank — the Pairs minigame's treat (specs/pairs-mode.md §3.1). One earned counter
  // is enough here, unlike the UFO's unlocked/bonus split above: that split exists only because
  // `ufosUnlocked` is a high-water mark a bonus would raise. The Diamond has a single source
  // (reaching Saturn in one game) and no mark to protect, so a plain counter is correct.
  // Absent on profiles written before this feature — always read with `?? 0`.
  diamondsEarned: number;
  diamondsClaimed: number;
  // 🧊 Ice Cube bank — the Ice Maze minigame's treat (specs/word-maze-mode.md §3.1). Same shape as
  // the Diamond pair above: one source (reaching Neptune in one game), no high-water mark, so a
  // plain earned/claimed pair. Absent on profiles written before this feature — read with `?? 0`.
  cubesEarned: number;
  cubesClaimed: number;
  // 🌌 Galaxy bank — the Word Wagon minigame's treat (specs/wagon-mode.md §3.1). Same shape again:
  // one source (reaching the top of the planet ladder, GALAXY_STARS, in one game) and no
  // high-water mark. Absent on profiles written before this feature — read with `?? 0`.
  galaxiesEarned: number;
  galaxiesClaimed: number;
  // 🛰️ Satellite bank — the Word Labyrinth minigame's treat (specs/labyrinth-mode.md §3.1). Same
  // shape once more: one source (filling the star stack, STAR_STACK_SLOTS, in one game) and no
  // high-water mark. Absent on profiles written before this feature — read with `?? 0`.
  satellitesEarned: number;
  satellitesClaimed: number;
  // 🍝 Plate bank — the Recipe Kitchen's treat (specs/recipe-mode.md §3.1). Same shape once more:
  // one source (star 14, the eclipse, in a single game) and no high-water mark. Absent on profiles
  // written before this feature — read with `?? 0`.
  platesEarned: number;
  platesClaimed: number;
  // 🐱 Cat bank — the Cat & Mouse letter hunt's treat (specs/cat-mouse-mode.md §3.1). Same shape
  // as every bank since the Diamond: one source (star 2, Venus, in a single game) and no
  // high-water mark. Absent on profiles written before this feature — read with `?? 0`.
  catsEarned: number;
  catsClaimed: number;
  // 🪱 Space Worm bank — the letter-eating worm's treat (specs/space-worm-mode.md §3.1). Same shape
  // once more: one source (star 13, the red Sun, in a single game) and no high-water mark. Absent
  // on profiles written before this feature — read with `?? 0`.
  wormsEarned: number;
  wormsClaimed: number;
  // 🚀 Rocket bank — Rocket Read's treat (specs/rocket-read-mode.md §3.1). One source (star 15, the
  // violet Sun, in a single game). Absent on profiles written before this feature — read with `?? 0`.
  rocketsEarned: number;
  rocketsClaimed: number;
  // 🏆 Scoreboard — the lesson's record card (specs/scoreboard.md §4), opened from the reward icon
  // at the end of the progress bar. Four independent marks, all per lesson and all absent on
  // profiles written before the feature, so every read goes through emptyProfile()'s defaults.
  //
  // `bestRoundMs` is deliberately NOT the global `abcs.bestRoundMs.v1` the HUD shows: that one is
  // a single number across ten lessons of different shapes, so a record set in Picture Words would
  // stand unbeatable on the sentence lesson's card (§1.1). The two are allowed to disagree.
  /** Fastest board CLEARED in this lesson, ms. Null until the first clear. */
  bestRoundMs: number | null;
  /** Highest round number REACHED in one game — the round the game ended on, not the last cleared. */
  maxRound: number;
  /** Boards cleared in this lesson, lifetime. */
  totalRounds: number;
  /** Most planet-stack stars earned in one game (GALAXY_STARS = 17 is the top of the ladder). */
  bestStackStars: number;
}

export interface MasteryData {
  v: 1;
  sessionCounter: number;
  profile: ProfileStat;
  words: Record<string, WordStat>;
  // ── board rotation, persisted so a new game resumes instead of restarting ──
  // A game ends on lives, and RoundScene builds a fresh ReadingProgression for each one. While
  // these lived in memory, every game re-entered the topic round-robin at drillClasses[0] and with
  // an empty word-recency history — Daily Life always opened on `colors`, Adventure on `bigcats`.
  /** Monotonic board counter for this lesson. The clock WordStat.lastBoardSeq is measured against. */
  boardSeq: number;
  /** Topic (drill class) of the last board built, so the round-robin continues across games. */
  lastClassId: string | null;
}

const KEY_PREFIX = 'abcs.mastery.v1.';

/** `storeKey` is a lesson key (`packKey`, e.g. `reading-250__de`) — see file header. */
function keyFor(storeKey: string): string {
  return KEY_PREFIX + storeKey;
}

export function emptyProfile(): ProfileStat {
  return {
    firstPlayedAt: null,
    lastPlayedAt: null,
    totalCorrect: 0,
    totalWrong: 0,
    days: [],
    abcPlanetReachedAt: null,
    celebrationShownAt: null,
    ufosUnlocked: 0,
    ufosBonus: 0,
    ufosClaimed: 0,
    diamondsEarned: 0,
    diamondsClaimed: 0,
    cubesEarned: 0,
    cubesClaimed: 0,
    galaxiesEarned: 0,
    galaxiesClaimed: 0,
    satellitesEarned: 0,
    satellitesClaimed: 0,
    platesEarned: 0,
    platesClaimed: 0,
    catsEarned: 0,
    catsClaimed: 0,
    wormsEarned: 0,
    wormsClaimed: 0,
    rocketsEarned: 0,
    rocketsClaimed: 0,
    bestRoundMs: null,
    maxRound: 0,
    totalRounds: 0,
    bestStackStars: 0,
  };
}

export function emptyMastery(): MasteryData {
  return {
    v: 1, sessionCounter: 0, profile: emptyProfile(), words: {},
    boardSeq: 0, lastClassId: null,
  };
}

// ---------- Write coalescing ----------
// saveMastery is called on every pop (ReadingProgression.recordHit). A lesson's blob is ~46 KB, so
// that was a JSON.stringify plus a synchronous localStorage write per tap. Chrome absorbs it
// (~0.4 ms, in-memory with an async flush), but iOS WKWebView routes every write through a
// synchronous IPC to the storage process backed by SQLite — during a fast volley those pile up and
// the game visibly freezes for a moment.
//
// So writes are coalesced: keep the latest data per key in memory and flush at most once per
// FLUSH_DELAY_MS. The pending map is authoritative for reads, and everything is flushed when the
// page hides, so nothing is lost — losing a few hundred ms of progress isn't possible, only
// deferring it.

const FLUSH_DELAY_MS = 400;

/** storage key → latest data not yet written. Read by loadMastery so it never sees stale bytes. */
const pending = new Map<string, MasteryData>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let hideHooked = false;

/** Write every pending blob out now. Safe to call at any time; a no-op when nothing is pending. */
export function flushMastery(): void {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (pending.size === 0) return;
  let wrote = false;
  for (const [key, data] of pending) {
    try {
      localStorage.setItem(key, JSON.stringify(data));
      wrote = true;
    } catch (err) {
      console.warn('[mastery] failed to persist progress:', err);
    }
  }
  pending.clear();
  // First time anything actually lands on disk, ask the browser to keep it — see there for why
  // the question waits until now rather than being asked at boot. No-op on every later flush.
  if (wrote) requestPersistentStorage();
}

/**
 * Flush before the page can go away. `pagehide` is the reliable one on iOS (`beforeunload` does not
 * fire there), and `visibilitychange` covers the home button / app switcher, which on a phone is how
 * a session usually ends.
 */
function hookFlushOnHide(): void {
  if (hideHooked || typeof window === 'undefined') return;
  hideHooked = true;
  window.addEventListener('pagehide', flushMastery);
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) flushMastery();
    });
  }
}

export function loadMastery(storeKey: string): MasteryData {
  try {
    const key = keyFor(storeKey);
    // An unflushed write is newer than whatever is in localStorage.
    const queued = pending.get(key);
    if (queued) return queued;
    const raw = localStorage.getItem(key);
    if (!raw) return emptyMastery();
    const parsed = JSON.parse(raw) as MasteryData;
    if (!parsed || parsed.v !== 1 || typeof parsed.words !== 'object') return emptyMastery();
    // A save written before `celebrationShownAt` existed that already reached the planet has
    // necessarily seen (or forfeited) its celebration long ago — back-fill it as shown so the
    // one-time screen doesn't re-fire for everyone who already finished a lesson. The missing KEY
    // is the discriminator, not a null value: a fresh completion always happens after this load,
    // so `reached && key absent` can only mean an old save.
    const stored = parsed.profile as Partial<ProfileStat> | undefined;
    if (stored && stored.abcPlanetReachedAt != null && !('celebrationShownAt' in stored)) {
      stored.celebrationShownAt = stored.abcPlanetReachedAt;
    }
    // Defensive: ensure profile shape (forward-compatible with older saves).
    parsed.profile = { ...emptyProfile(), ...parsed.profile };
    if (typeof parsed.sessionCounter !== 'number') parsed.sessionCounter = 0;
    // Rotation state postdates the first saves; a blob without it starts the clock at 0, which
    // just means every word reads as "not seen recently" for one board. Per-word back-fill is
    // lazy (statOf), so a 250-word blob isn't rewritten on load.
    if (typeof parsed.boardSeq !== 'number') parsed.boardSeq = 0;
    if (typeof parsed.lastClassId !== 'string') parsed.lastClassId = null;
    clampBoxes(parsed);
    return parsed;
  } catch {
    return emptyMastery();
  }
}

/**
 * Fold saves written when the Leitner ladder was five boxes long onto the current MAX_BOX.
 *
 * The ladder shrank to 4 (see BOX_WEIGHTS) so a word costs three correct answers rather than four.
 * That makes the old box 5 and the old box 4 both "mastered" under the new bar, and every stat
 * reader — isMasteredStat, weightFor, applyResult — already treats them that way. Clamping only
 * keeps the STORED value inside the schema `1..MAX_BOX` so a blob round-trips honestly instead of
 * carrying a number no code can produce any more.
 *
 * Consequence, deliberate and in the player's favour: on the first load after the update, words
 * that sat one answer short of mastery are counted as mastered, so the journey bar jumps forward
 * once. It never moves backwards — the clamp cannot lower a word below MASTER_BOX.
 */
function clampBoxes(data: MasteryData): void {
  for (const s of Object.values(data.words)) {
    if (typeof s?.box === 'number' && s.box > MAX_BOX) s.box = MAX_BOX;
  }
}

/**
 * Queue a lesson's progress for persistence. The write itself happens within FLUSH_DELAY_MS (or
 * immediately, on page hide) — see the write-coalescing note above. Call `flushMastery()` when the
 * write must land now.
 */
export function saveMastery(storeKey: string, data: MasteryData): void {
  pending.set(keyFor(storeKey), data);
  hookFlushOnHide();
  // A plain timeout, not a debounce: staleness stays bounded by FLUSH_DELAY_MS however fast the
  // taps come, instead of a long volley pushing the write out indefinitely.
  if (flushTimer === null) flushTimer = setTimeout(flushMastery, FLUSH_DELAY_MS);
}

/** Wipe one lesson's progress. Backs the menu "Reset progress" action. */
export function resetMastery(storeKey: string): void {
  try {
    pending.delete(keyFor(storeKey));   // don't let a queued write resurrect it
    localStorage.removeItem(keyFor(storeKey));
  } catch (err) {
    console.warn('[mastery] failed to reset progress:', err);
  }
}

/**
 * One-time forward migration: if `storeKey` has no saved data yet but the older `legacyKey`
 * does, move the legacy data under the new key (and remove the old entry). No-op if the new
 * key already has data or the legacy key is absent. Used to carry pre-per-lesson reading
 * progress (keyed by bare language tag) onto its lesson key.
 */
export function migrateMasteryKey(legacyKey: string, storeKey: string): void {
  if (legacyKey === storeKey) return;
  flushMastery();   // this reads localStorage directly, so no write may still be queued
  try {
    if (localStorage.getItem(keyFor(storeKey)) !== null) return;   // already migrated/started
    const legacy = localStorage.getItem(keyFor(legacyKey));
    if (legacy === null) return;
    localStorage.setItem(keyFor(storeKey), legacy);
    localStorage.removeItem(keyFor(legacyKey));
  } catch (err) {
    console.warn('[mastery] failed to migrate progress:', err);
  }
}
